<template>
  <FormLayout title="HR-DOS | Leave Application">
      
    <!-- FORM WRAPPER (Pointer events disabled during loading) -->
    <div class="form-wrapper" :style="isLoading ? 'pointer-events: none;' : ''">
      
      <!-- ========================================================================================= -->
      <!-- DESKTOP / LARGE SCREEN FORM (Strict Paper Replica - 100% untouched) -->
      <!-- ========================================================================================= -->
      <div class="hide-on-med-and-down">
        <div class="paper-form z-depth-2 white" :class="{ 'read-only-mode': currentStep === 2 }" style="position: relative;">
          
          <!-- MINIMALIST OVERLAY LOADER (Matches the form size exactly) -->
          <div v-if="isLoading" class="loader-overlay">
            <div class="minimal-loader">
              <span class="loader-text">Loading your information...</span>
              <div class="loader-line"></div>
            </div>
          </div>
          
          <div class="form-header flex-row" style="align-items: flex-start;">
            
            <div class="header-left">
              <p style="text-align: left; font-style: italic; margin: 0;">Civil Service Form No. 6<br>Revised 2020</p>
            </div>
            <div style="margin-top: 45px; margin-left: -90px; display: flex; gap: 10px;">
                <img src="@/assets/bagong-pilipinas-logo.png" alt="Bagong Pilipinas" class="header-logo" @error="$event.target.style.display='none'">
                <img src="@/assets/LTOlogo.png" alt="LTO" class="header-logo" @error="$event.target.style.display='none'">
              </div>
            
              <div class="header-center">
                <p class="republic-text">
                  Republic of the Philippines<br>
                  Department of Transportation<br>
                  <b>LAND TRANSPORTATION OFFICE</b><br>
                  Regional Office No. 02<br>
                  Tuguegarao City, Cagayan
                </p>
                <h5 class="form-title" style="margin-left: -165px;"><b>APPLICATION FOR LEAVE</b></h5>
              </div>
            
            <div class="header-right right-align">
              <p style="margin: 0 60px 10px 0; font-size: 1rem"><b>ANNEX A</b></p>
              <div class="stamp-box">Stamp of Date of Receipt</div>
            </div>
          </div>

          <div class="form-inputs">
            
            <div class="row1and2">
              <div class="form-row flex-row" style="padding-top: 2px;">
                <div class="form-col w-30 padding-sm" style="margin-right: 50px;">
                  <label class="cs-label" :class="{ 'error-text': errors.office }">1. OFFICE/DEPARTMENT</label>
                  <input ref="office" type="text" class="cs-input" v-model="form.office" @input="errors.office = false" :readonly="currentStep === 2">
                </div>
                <div class="form-col w-70 padding-sm" style="display: flex;">
                  <label style="margin-right: 60px;" class="cs-label">2. NAME:</label>
                  <div class="name-grid">
                    <div>
                      <div class="sub-label center-align" :class="{ 'error-text': errors.lastName }">(Last)</div>
                      <input ref="lastName" type="text" class="cs-input center-align" v-model="form.lastName" @input="errors.lastName = false" :readonly="currentStep === 2">
                    </div>
                    <div>
                      <div class="sub-label center-align" :class="{ 'error-text': errors.firstName }">(First)</div>
                      <input ref="firstName" type="text" class="cs-input center-align" v-model="form.firstName" @input="errors.firstName = false" :readonly="currentStep === 2">
                    </div>
                    <div>
                      <div class="sub-label center-align">(Middle)</div>
                      <input ref="middleName" type="text" class="cs-input center-align" v-model="form.middleName" :readonly="currentStep === 2">
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="form-row border-top flex-row">
                <div class="form-col w-35 padding-sm" style="display: flex; margin-right: 60px;">
                  <label class="cs-label for-row2" :class="{ 'error-text': errors.dateOfFiling }" style="margin-right: 70px; margin-top: 8px;">3. DATE OF FILING</label>
                  <input ref="dateOfFiling" type="date" class="cs-input" v-model="form.dateOfFiling" @input="errors.dateOfFiling = false" :readonly="currentStep === 2" disabled>
                </div>
                <div class="form-col w-45 padding-sm" style="display: flex; margin-right: 40px;">
                  <label class="cs-label for-row2" :class="{ 'error-text': errors.position }" style="margin-right: 5px; margin-top: 8px;">4. POSITION</label>
                  <input ref="position" type="text" class="cs-input" v-model="form.position" @input="errors.position = false" :readonly="currentStep === 2">
                </div>
                <div class="form-col w-20 padding-sm" style="display: flex;">
                  <label class="cs-label for-row2" style="margin-right: 5px; margin-top: 8px;">5. SALARY</label>
                  <input ref="salary" type="number" class="cs-input" v-model="form.salary" :readonly="currentStep === 2">
                </div>
              </div>
            </div>
            
            <div class="form-row section-header center-align row3">
              <b>6. DETAILS OF APPLICATION</b>
            </div>

            <div class="form-row flex-row row4">
              <div class="form-col w-55 border-right padding-md">
                <label class="cs-label" :class="{ 'error-text': errors.leaveType }" style="margin-bottom: 10px;">6.A TYPE OF LEAVE TO BE AVAILED OF</label>
                <div class="cs-custom-radios">
                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Vacation Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Vacation Leave</strong> <small>(Sec. 51, Rule XVI, Omnibus Rules Implementing E.O. No. 292)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Mandatory/Forced Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Mandatory/Forced Leave</strong> <small>(Sec. 25, Rule XVI, Omnibus Rules Implementing E.O. No. 292)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Sick Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Sick Leave</strong> <small>(Sec. 43, Rule XVI, Omnibus Rules Implementing E.O. No. 292)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Maternity Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Maternity Leave</strong> <small>(R.A. No. 11210 / IRR issued by CSC, DOLE and SSS)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Paternity Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Paternity Leave</strong> <small>(R.A. No. 8187 / CSC MC No. 71, s. 1998, as amended)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Special Privilege Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Special Privilege Leave</strong> <small>(Sec. 21, Rule XVI, Omnibus Rules Implementing E.O. No. 292)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Solo Parent Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Solo Parent Leave</strong> <small>(RA No. 8972 / CSC MC No. 8, s. 2004)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Study Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Study Leave</strong> <small>(Sec. 68, Rule XVI, Omnibus Rules Implementing E.O. No. 292)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="10-Day VAWC Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>10-Day VAWC Leave</strong> <small>(RA No. 9262 / CSC MC No. 15, s. 2005)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Rehabilitation Privilege" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Rehabilitation Privilege</strong> <small>(Sec. 55, Rule XVI, Omnibus Rules Implementing E.O. No. 292)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Special Leave Benefits for Women" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Special Leave Benefits for Women</strong> <small>(RA No. 9710 / CSC MC No. 25, s. 2010)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Special Emergency Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Special Emergency (Calamity) Leave</strong> <small>(CSC MC No. 2, s. 2012, as amended)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row">
                    <input ref="leaveType" type="radio" value="Adoption Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">
                      <strong>Adoption Leave</strong> <small>(R.A. No. 8552)</small>
                    </div>
                  </label>

                  <label class="cs-radio-row others-row" style="margin-bottom: 20px !important">
                    <input ref="leaveType" type="radio" value="Others" v-model="form.leaveType" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text others-text-wrapper">
                      <i>Others:</i>
                      <input :disabled="form.leaveType !== 'Others' || currentStep === 2" type="text" class="cs-input inline-line thick-line" v-model="form.leaveTypeOthers" :readonly="currentStep === 2" style="flex: 1; margin: 0 10px !important;">
                    </div>
                  </label>
                </div>
              </div>

              <div class="form-col w-45 padding-md">
                <label class="cs-label" :class="{ 'error-text': errors.leaveDetails }" style="margin-bottom: 10px;">6.B DETAILS OF LEAVE</label>
            
                <div class="sub-section">
                  <p style="text-align: left; font-style: italic; margin: 3px 0 3px 18px;">In case of Vacation/Special Privilege Leave:</p>
                  <div class="cs-custom-radios">
                    <label class="cs-radio-row">
                      <input ref="leaveDetails" type="radio" value="Within the Philippines" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Vacation Leave'">
                      <div class="cs-box"></div>
                      <div class="cs-text others-text-wrapper">
                        <span><strong>Within the Philippines</strong></span>
                        <input :disabled="form.leaveDetails !== 'Within the Philippines' || currentStep === 2"  type="text" class="cs-input inline-line thick-line" v-model="form.leaveDetailsSpecifyPH" :readonly="currentStep === 2" style="flex: 1; margin-left: 10px !important;">
                      </div>
                    </label>
                    <label class="cs-radio-row others-row">
                      <input ref="leaveDetails" type="radio" value="Abroad" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Vacation Leave'">
                      <div class="cs-box"></div>
                      <div class="cs-text others-text-wrapper">
                        <span><strong>Abroad (Specify)</strong></span>
                        <input :disabled="form.leaveDetails !== 'Abroad' || currentStep === 2" type="text" class="cs-input inline-line thick-line" v-model="form.leaveDetailsSpecifyAbroad" :readonly="currentStep === 2" style="flex: 1; margin-left: 10px !important;">
                      </div>
                    </label>
                  </div>
                </div>

                <div class="sub-section">
                  <p style="text-align: left; font-style: italic; margin: 3px 0 3px 18px;">In case of Sick Leave:</p>
                  <div class="cs-custom-radios">
                    <label class="cs-radio-row others-row">
                      <input ref="leaveDetails" type="radio" value="In Hospital" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Sick Leave'">
                      <div class="cs-box"></div>
                      <div class="cs-text others-text-wrapper">
                        <span><strong>In Hospital (Specify Illness)</strong></span>
                        <input :disabled="form.leaveDetails !== 'In Hospital' || currentStep === 2" type="text" class="cs-input inline-line thick-line" v-model="form.sickSpecifyHospital" :readonly="currentStep === 2" style="flex: 1; margin-left: 10px !important;">
                      </div>
                    </label>
                    <label class="cs-radio-row others-row">
                      <input ref="leaveDetails" type="radio" value="Out Patient" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Sick Leave'">
                      <div class="cs-box"></div>
                      <div class="cs-text others-text-wrapper">
                        <span><strong>Out Patient (Specify Illness)</strong></span>
                        <input :disabled="form.leaveDetails !== 'Out Patient' || currentStep === 2" type="text" class="cs-input inline-line thick-line" v-model="form.sickSpecifyOutPatient" :readonly="currentStep === 2" style="flex: 1; margin-left: 10px !important;">
                      </div>
                    </label>
                  </div>
                </div>

                <div class="sub-section">
                  <p style="text-align: left; font-style: italic; margin: 3px 0 3px 18px;">In case of Special Leave Benefits for Women:</p>
                  <div class="flex-row valign-wrapper" style="margin-left: 10px;">
                    <p style="text-align: left; margin: 3px 0 3px 18px;">(Specify Illness):</p>
                    <input :disabled="form.leaveType !== 'Special Leave Benefits for Women' || currentStep === 2" type="text" class="cs-input inline-line thick-line" v-model="form.womenSpecify" :readonly="currentStep === 2" style="flex: 1; margin: 0 10px !important; margin-right: 10px !important;">
                  </div>
                </div>

                <div class="sub-section">
                  <p  style="text-align: left; font-style: italic; margin: 3px 0 3px 18px;">In case of Study Leave:</p>
                  <div class="cs-custom-radios">
                    <label class="cs-radio-row">
                      <input ref="leaveDetails" type="radio" value="Completion of Master's Degree" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Study Leave'">
                      <div class="cs-box"></div>
                      <div class="cs-text"><strong>Completion of Master's Degree</strong></div>
                    </label>
                    <label class="cs-radio-row">
                      <input ref="leaveDetails" type="radio" value="BAR/Board Examination Review" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Study Leave'">
                      <div class="cs-box"></div>
                      <div class="cs-text"><strong>BAR/Board Examination Review</strong></div>
                    </label>
                  </div>
                </div>

                <div class="sub-section">
                  <div class="flex-row valign-wrapper" style="margin-left: 10px;">
                    <p style="text-align: left; font-style: italic; margin: 3px 0 3px 18px;">Other purpose:</p>
                    <input ref="leaveDetails" type="text" class="cs-input inline-line thick-line" v-model="form.othersspecify" :readonly="currentStep === 2" style="flex: 1; margin-left: 10px !important; margin-right: 10px !important;">
                  </div>
                  
                  <div class="cs-custom-radios">
                    <label class="cs-radio-row">
                      <input ref="leaveDetails" type="radio" value="Monetization" v-model="form.leaveDetails" :disabled="currentStep === 2">
                      <div class="cs-box"></div>
                      <div class="cs-text">Monetization of Leave Credits</div>
                    </label>
                    <label class="cs-radio-row">
                      <input ref="leaveDetails" type="radio" value="Terminal" v-model="form.leaveDetails" :disabled="currentStep === 2">
                      <div class="cs-box"></div>
                      <div class="cs-text">Terminal Leave</div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-row flex-row row5">
              <div class="form-col w-55 border-right padding-md">
                <label class="cs-label" :class="{ 'error-text': errors.workingDays }">6.C NUMBER OF WORKING DAYS APPLIED FOR</label>
                <input ref="workingDays" style="width: 85%; padding-left: 10px !important;" type="number" step="0.5" class="cs-input inline-line" v-model="form.workingDays" @input="errors.workingDays = false" :readonly="currentStep === 2">
                
                <div class="flex-row valign-wrapper" style="margin-top: 15px; margin-left: 38px;">
                  <label class="cs-label" :class="{ 'error-text': errors.inclusiveDates }" style="margin: 0;">INCLUSIVE DATES</label>
                  <button 
                    v-if="currentStep === 1"
                    type="button" 
                    class="btn-small indigo darken-3" 
                    style="margin-left: 15px; border-radius: 12px; height: 24px; line-height: 24px; font-size: 0.7rem; padding: 0 10px;"
                    @click="showDatePicker = !showDatePicker"
                  >
                    <i class="material-icons left" style="font-size: 0.9rem; margin-right: 5px;">date_range</i> 
                    {{ form.inclusiveDates ? 'Change Dates' : 'Select Dates' }}
                  </button>
                </div>

                <div v-if="showDatePicker && currentStep === 1" class="z-depth-1 form-wrapper" style="padding: 10px; margin-top: 10px; border-radius: 8px; width: 85%; background-color: #f9f9f9; margin-left: 38px;">
                  <div class="flex-row" style="gap: 15px;">
                    <div style="flex: 1;">
                      <label class="sub-label">From:</label>
                      <input type="date" class="cs-input" v-model="dateFrom" :min="form.dateOfFiling" style="border-bottom: 1px solid #ccc !important;">
                    </div>
                    <div style="flex: 1;">
                      <label class="sub-label">To:</label>
                      <input type="date" class="cs-input" v-model="dateTo" :min="dateFrom" style="border-bottom: 1px solid #ccc !important;">
                    </div>
                  </div>
                  <div class="right-align" style="margin-top: 10px;">
                    <button type="button" class="btn-flat btn-small" @click="showDatePicker = false" style="margin-right: 10px;">Cancel</button>
                    <button type="button" class="btn-small green darken-2" @click="formatInclusiveDates">Confirm</button>
                  </div>
                </div>
                
                <input ref="inclusiveDates" style="width: 85%; padding-left: 10px !important; margin-top: 5px !important;" type="text" class="cs-input inline-line" v-model="form.inclusiveDates" @input="errors.inclusiveDates = false" :readonly="currentStep === 2">
                </div>
            
              <div class="form-col w-45 padding-md">
                <label class="cs-label" :class="{ 'error-text': errors.commutation }" style="margin-bottom: 10px;">6.D COMMUTATION</label>
                
                <div class="cs-custom-radios">
                  <label class="cs-radio-row">
                    <input ref="commutation" type="radio" value="Not Requested" v-model="form.commutation" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">Not Requested</div>
                  </label>
                  <label class="cs-radio-row">
                    <input ref="commutation" type="radio" value="Requested" v-model="form.commutation" :disabled="currentStep === 2">
                    <div class="cs-box"></div>
                    <div class="cs-text">Requested</div>
                  </label>
                </div>
            
                <div class="signature-box center-align" style="margin-top: 30px; margin-bottom: 10px;">
                  <div v-if="signatureData" style="height: 50px; margin-bottom: 5px;">
                    <img :src="signatureData" alt="Signature" style="max-height: 100%; object-fit: contain;">
                  </div>
                  <button 
                    v-if="currentStep === 1" 
                    type="button" 
                    class="btn-small indigo darken-3" 
                    @click="openSignatureModal" 
                    style="margin-bottom: 5px; border-radius: 4px; padding: 0 10px;"
                  >
                    <i class="material-icons left" style="margin-right: 5px;">draw</i> 
                    {{ signatureData ? 'Retake Signature' : 'Draw Signature' }}
                  </button>
                  <div style="width: 80%;" class="signature-line" :class="{ 'error-text border-red': errors.signature }"></div>
                  <div class="sub-label" :class="{ 'error-text': errors.signature }">(Signature of Applicant)</div>
                </div>
              </div>
            </div>

            <div class="form-row section-header center-align row6">
              <b>7. DETAILS OF ACTION ON APPLICATION</b>
            </div>

            <div class="form-row flex-row row7">
              <div class="form-col w-55 border-right padding-md">
                <label class="cs-label" style="margin-bottom: 5px;">7.A CERTIFICATION OF LEAVE CREDITS</label>
                
                <div class="flex-row center-content">
                  <span style="font-size: 0.85rem; margin-right: 10px; margin-left: 127px;">As of</span>
                  <input disabled type="text" class="cs-input inline-line thick-line" v-model="form.actionAsOf" :readonly="currentStep === 2" style="width: 329px; text-align: center; margin-bottom: 0 !important;">
                </div>

                <table class="cert-table">
                  <thead>
                    <tr>
                      <th width="40%"></th>
                      <th>Vacation Leave</th>
                      <th>Sick Leave</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Total Earned</td>
                      <td><input disabled type="text" class="table-input" v-model="form.vacationEarned" :readonly="currentStep === 2"></td>
                      <td><input disabled type="text" class="table-input" v-model="form.sickEarned" :readonly="currentStep === 2"></td>
                    </tr>
                    <tr>
                      <td>Less this application</td>
                      <td><input disabled type="text" class="table-input" v-model="form.vacationLess" :readonly="currentStep === 2"></td>
                      <td><input disabled type="text" class="table-input" v-model="form.sickLess" :readonly="currentStep === 2"></td>
                    </tr>
                    <tr>
                      <td>Balance</td>
                      <td><input disabled type="text" class="table-input" v-model="form.vacationBalance" :readonly="currentStep === 2"></td>
                      <td><input disabled type="text" class="table-input" v-model="form.sickBalance" :readonly="currentStep === 2"></td>
                    </tr>
                  </tbody>
                </table>

                <div class="signature-box center-align" style="margin-top: 50px; margin-bottom: 20px;">
                  <div class="signature-line" style="width: 80%;"></div>
                  <div class="sub-label" style="margin-bottom: 10px;">Administrative Officer V (HRMO III)</div>
                </div>
              </div>

              <div class="form-col w-45 padding-md">
                <label class="cs-label" style="margin-bottom: 15px;">7.B RECOMMENDATION</label>
                
                <div class="cs-custom-radios">
                  <label class="cs-radio-row" style="align-items: flex-start;">
                    <input disabled type="radio" value="For approval" v-model="form.recommendation">
                    <div class="cs-box" style="height: 29px; margin-bottom: 0;"></div>
                    <div class="cs-text" style="padding: 0 0 5px 0; line-height: 24px; margin-top: 8px !important;">For approval</div>
                  </label>
                  
                  <label class="cs-radio-row" style="align-items: flex-start;">
                    <input disabled type="radio" value="For disapproval" v-model="form.recommendation">
                    <div class="cs-box" style="height: 35px; border-bottom: 3px solid #000; margin-top: -8px !important;"></div>
                    <div class="cs-text" style="width: 100%; padding: 0;">
                      
                      <div style="display: flex; align-items: flex-end; margin-bottom: 5px;">
                        <p style="white-space: nowrap; margin-right: 5px !important; margin-top: 5px !important; margin-bottom: 8px !important; line-height: 1.2;">For disapproval due to</p>
                        <input disabled 
                          ref="reason1"
                          type="text" 
                          class="cs-input" 
                          v-model="form.disapprovalReason1" 
                          :readonly="currentStep === 2" 
                          style="flex: 1; border-bottom: 2px solid #000 !important; height: 1.5rem !important; text-align: left !important;" 
                          maxlength="29"
                          @input="handleAutoTab(form.disapprovalReason1, 29, 'reason2')"
                        >
                      </div>
                      
                      <input disabled 
                        ref="reason2"
                        type="text" 
                        class="cs-input" 
                        v-model="form.disapprovalReason2" 
                        :readonly="currentStep === 2" 
                        style="width: 100%; border-bottom: 2px solid #000 !important; height: 1.5rem !important; text-align: left !important; margin-bottom: 5px !important;" 
                        maxlength="51"
                        @input="handleAutoTab(form.disapprovalReason2, 51, 'reason3')"
                      >
                      <input disabled 
                        ref="reason3"
                        type="text" 
                        class="cs-input" 
                        v-model="form.disapprovalReason3" 
                        :readonly="currentStep === 2" 
                        style="width: 100%; border-bottom: 2px solid #000 !important; height: 1.5rem !important; text-align: left !important;" 
                        maxlength="51"
                      >
                    </div>
                  </label>
                </div>

                <div class="signature-box center-align" style="margin-top: 47px;">
                  <div class="signature-line" style="width: 80%;"></div>
                  <div class="sub-label" style="margin-bottom: 10px;">{{ form.position === "CHIEF ADMINISTRATIVE OFFICER" ? "Assistant Regional Director" : "Immediate Supervisor"}}</div>
                </div>
              </div>
            </div>

            <div class="form-row flex-row row8">
              <div class="form-col w-55 padding-md border-right">
                <label class="cs-label" style="margin-bottom: 10px;">7.C APPROVED FOR:</label>
                
                <div class="flex-row valign-wrapper" style="margin-bottom: 5px;">
                  <input disabled type="text" class="cs-input inline-line thick-line" v-model="form.approvedDaysWithPay" :readonly="currentStep === 2" style="width: 60px; text-align: center;">
                  <span style="font-size: 0.85rem; color: #000; margin-left: 10px;">days with pay</span>
                </div>
                <div class="flex-row valign-wrapper" style="margin-bottom: 5px;">
                  <input disabled type="text" class="cs-input inline-line thick-line" v-model="form.approvedDaysWithoutPay" :readonly="currentStep === 2" style="width: 60px; text-align: center;">
                  <span style="font-size: 0.85rem; color: #000; margin-left: 10px;">days without pay</span>
                </div>
                <div class="flex-row valign-wrapper">
                  <input disabled type="text" class="cs-input inline-line thick-line" v-model="form.approvedOthers" :readonly="currentStep === 2" style="width: 60px; text-align: center;">
                  <span style="font-size: 0.85rem; color: #000; margin-left: 10px;">others (Specify)</span>
                </div>
              </div>

              <div class="form-col w-45 padding-md">
                <div style="display: flex; align-items: flex-end;">
                  <label class="cs-label" style="margin-bottom: 10px;">7.D DISAPPROVED DUE TO: </label>
                  <input 
                    ref="finalReason1"
                    type="text" 
                    class="cs-input" 
                    v-model="form.finalDisapprovalReason1" 
                    disabled
                    style="width: 100%; border-bottom: 2px solid #000 !important; height: 1.5rem !important; margin-left: 50px !important; text-align: left !important;"
                    maxlength="30"
                    @input="handleAutoTab(form.finalDisapprovalReason1, 29, 'finalReason2')"
                  >
                </div>
                <input 
                  ref="finalReason2"
                  type="text" 
                  class="cs-input" 
                  v-model="form.finalDisapprovalReason2"
                  disabled
                  style="width: 100%; border-bottom: 2px solid #000 !important; height: 1.5rem !important; margin-bottom: 4px !important; text-align: left !important;"
                  maxlength="57"
                  @input="handleAutoTab(form.finalDisapprovalReason2, 57, 'finalReason3')"
                  @keydown="handleBackspace($event, form.finalDisapprovalReason2, 'finalReason1')"
                >
                <input 
                  ref="finalReason3"
                  type="text" 
                  class="cs-input" 
                  v-model="form.finalDisapprovalReason3" 
                  disabled
                  style="width: 100%; border-bottom: 2px solid #000 !important; height: 1.5rem !important; margin-bottom: 4px !important; text-align: left !important;"
                  maxlength="57"
                  @input="handleAutoTab(form.finalDisapprovalReason3, 57, 'finalReason4')"
                  @keydown="handleBackspace($event, form.finalDisapprovalReason3, 'finalReason2')"
                >
                <input 
                  ref="finalReason4"
                  type="text" 
                  class="cs-input" 
                  v-model="form.finalDisapprovalReason4"
                  disabled
                  style="width: 100%; border-bottom: 2px solid #000 !important; height: 1.5rem !important; text-align: left !important;"
                  maxlength="57"
                  @keydown="handleBackspace($event, form.finalDisapprovalReason4, 'finalReason3')"
                >
              </div>
            </div>
            
            <div class="form-row row9 center-align padding-md">
              <div class="signature-box" style="margin-top: 25px; margin-bottom: 20px;">
                <div class="signature-line" style="width: 60%; margin: 0 auto;"></div>
              </div>
              
              <div v-if="currentStep === 1" style="margin-top: 30px; margin-bottom: 10px;">
                <button data-target="uploadModal" class="btn modal-trigger grey darken-3 waves-light">
                  <i class="material-icons left">attach_file</i> Upload Medical Cert / Supporting Docs (Optional)
                </button>
              </div>
              
              <div v-if="form.supportingDocument" class="green-text text-darken-2" style="margin-top: 10px; margin-bottom: 10px; font-weight: bold;">
                <i class="material-icons tiny">check_circle</i> Attached: {{ form.supportingDocument.name }}
              </div>

            </div>

          </div>
        </div>
      </div>

      <!-- ========================================================================================= -->
      <!-- MOBILE / SMALL SCREEN FORM (Clean Stacked Layout) -->
      <!-- ========================================================================================= -->
      <div class="hide-on-large-only">
        <div class="mobile-paper-form z-depth-1 white" :class="{ 'read-only-mode': currentStep === 2 }">
          
          <!-- MINIMALIST OVERLAY LOADER -->
          <div v-if="isLoading" class="loader-overlay" style="border-radius: 8px;">
            <div class="minimal-loader">
              <span class="loader-text">Loading...</span>
              <div class="loader-line"></div>
            </div>
          </div>

          <div class="center-align" style="margin-bottom: 25px;">
            <img src="@/assets/LTOlogo.png" style="height: 60px; margin-bottom: 10px;">
            <h6 style="font-weight: bold; margin: 0; color: #1565c0;">APPLICATION FOR LEAVE</h6>
            <p style="font-size: 0.8rem; color: #777; margin-top: 5px;">Civil Service Form No. 6</p>
          </div>

          <div class="mobile-section">
            <label class="mobile-label" :class="{ 'error-text': errors.office }">1. OFFICE/DEPARTMENT</label>
            <input type="text" class="browser-default mobile-input" v-model="form.office" :readonly="currentStep === 2">
          </div>

          <div class="mobile-section">
            <label class="mobile-label">2. NAME</label>
            <div class="mobile-name-grid">
              <div style="flex: 1;">
                <label class="mobile-sub-label" :class="{ 'error-text': errors.lastName }">Last Name</label>
                <input type="text" class="browser-default mobile-input" v-model="form.lastName" :readonly="currentStep === 2">
              </div>
              <div style="flex: 1;">
                <label class="mobile-sub-label" :class="{ 'error-text': errors.firstName }">First Name</label>
                <input type="text" class="browser-default mobile-input" v-model="form.firstName" :readonly="currentStep === 2">
              </div>
              <div style="flex: 1;">
                <label class="mobile-sub-label">Middle Name</label>
                <input type="text" class="browser-default mobile-input" v-model="form.middleName" :readonly="currentStep === 2">
              </div>
            </div>
          </div>

          <div class="mobile-section">
            <label class="mobile-label" :class="{ 'error-text': errors.dateOfFiling }">3. DATE OF FILING</label>
            <input type="date" class="browser-default mobile-input" v-model="form.dateOfFiling" :readonly="currentStep === 2" disabled>
          </div>

          <div class="mobile-section">
            <label class="mobile-label" :class="{ 'error-text': errors.position }">4. POSITION</label>
            <input type="text" class="browser-default mobile-input" v-model="form.position" :readonly="currentStep === 2">
          </div>

          <div class="mobile-section">
            <label class="mobile-label">5. SALARY</label>
            <input type="number" class="browser-default mobile-input" v-model="form.salary" :readonly="currentStep === 2">
          </div>

          <div class="mobile-section-header">6. DETAILS OF APPLICATION</div>

          <div class="mobile-section">
            <label class="mobile-label" :class="{ 'error-text': errors.leaveType }">6.A TYPE OF LEAVE</label>
            <div class="mobile-radio-group">
              <label>
                <input type="radio" class="with-gap" value="Vacation Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Vacation Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Mandatory/Forced Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Mandatory/Forced Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Sick Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Sick Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Maternity Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Maternity Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Paternity Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Paternity Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Special Privilege Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Special Privilege Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Solo Parent Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Solo Parent Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Study Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Study Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="10-Day VAWC Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>10-Day VAWC Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Rehabilitation Privilege" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Rehabilitation Privilege</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Special Leave Benefits for Women" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Special Leave Benefits for Women</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Special Emergency Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Special Emergency Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Adoption Leave" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Adoption Leave</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Others" v-model="form.leaveType" :disabled="currentStep === 2">
                <span>Others (Specify):</span>
              </label>
              <input v-if="form.leaveType === 'Others'" type="text" class="browser-default mobile-input" v-model="form.leaveTypeOthers" :readonly="currentStep === 2" style="margin-top: 5px;">
            </div>
          </div>

          <div class="mobile-section">
            <label class="mobile-label" :class="{ 'error-text': errors.leaveDetails }">6.B DETAILS OF LEAVE</label>
            
            <p class="mobile-hint">In case of Vacation/Special Privilege Leave:</p>
            <div class="mobile-radio-group">
              <label>
                <input type="radio" class="with-gap" value="Within the Philippines" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Vacation Leave'">
                <span>Within the Philippines</span>
              </label>
              <input v-if="form.leaveDetails === 'Within the Philippines'" type="text" class="browser-default mobile-input" v-model="form.leaveDetailsSpecifyPH" :readonly="currentStep === 2" style="margin-top: 5px;">
              
              <label>
                <input type="radio" class="with-gap" value="Abroad" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Vacation Leave'">
                <span>Abroad (Specify)</span>
              </label>
              <input v-if="form.leaveDetails === 'Abroad'" type="text" class="browser-default mobile-input" v-model="form.leaveDetailsSpecifyAbroad" :readonly="currentStep === 2" style="margin-top: 5px;">
            </div>

            <p class="mobile-hint" style="margin-top: 15px;">In case of Sick Leave:</p>
            <div class="mobile-radio-group">
              <label>
                <input type="radio" class="with-gap" value="In Hospital" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Sick Leave'">
                <span>In Hospital (Specify Illness)</span>
              </label>
              <input v-if="form.leaveDetails === 'In Hospital'" type="text" class="browser-default mobile-input" v-model="form.sickSpecifyHospital" :readonly="currentStep === 2" style="margin-top: 5px;">
              
              <label>
                <input type="radio" class="with-gap" value="Out Patient" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Sick Leave'">
                <span>Out Patient (Specify Illness)</span>
              </label>
              <input v-if="form.leaveDetails === 'Out Patient'" type="text" class="browser-default mobile-input" v-model="form.sickSpecifyOutPatient" :readonly="currentStep === 2" style="margin-top: 5px;">
            </div>

            <p class="mobile-hint" style="margin-top: 15px;">In case of Special Leave Benefits for Women:</p>
            <div class="mobile-radio-group">
              <label class="mobile-sub-label">Specify Illness:</label>
              <input type="text" class="browser-default mobile-input" v-model="form.womenSpecify" :disabled="currentStep === 2 || form.leaveType !== 'Special Leave Benefits for Women'">
            </div>

            <p class="mobile-hint" style="margin-top: 15px;">In case of Study Leave:</p>
            <div class="mobile-radio-group">
              <label>
                <input type="radio" class="with-gap" value="Completion of Master's Degree" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Study Leave'">
                <span>Completion of Master's Degree</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="BAR/Board Examination Review" v-model="form.leaveDetails" :disabled="currentStep === 2 || form.leaveType !== 'Study Leave'">
                <span>BAR/Board Examination Review</span>
              </label>
            </div>

            <p class="mobile-hint" style="margin-top: 15px;">Other purpose:</p>
            <div class="mobile-radio-group">
              <label>
                <input type="radio" class="with-gap" value="Monetization" v-model="form.leaveDetails" :disabled="currentStep === 2">
                <span>Monetization of Leave Credits</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Terminal" v-model="form.leaveDetails" :disabled="currentStep === 2">
                <span>Terminal Leave</span>
              </label>
              <label class="mobile-sub-label" style="margin-top: 5px;">Specify other details:</label>
              <input type="text" class="browser-default mobile-input" v-model="form.othersspecify" :readonly="currentStep === 2">
            </div>
          </div>

          <div class="mobile-section">
            <label class="mobile-label" :class="{ 'error-text': errors.workingDays }">6.C NUMBER OF WORKING DAYS APPLIED FOR</label>
            <input type="number" step="0.5" class="browser-default mobile-input" v-model="form.workingDays" @input="errors.workingDays = false" :readonly="currentStep === 2">
            
            <label class="mobile-label" :class="{ 'error-text': errors.inclusiveDates }" style="margin-top: 15px;">INCLUSIVE DATES</label>
            <button 
              v-if="currentStep === 1"
              type="button" 
              class="btn indigo darken-3 mobile-btn" 
              @click="showDatePicker = !showDatePicker"
            >
              <i class="material-icons left">date_range</i> 
              {{ form.inclusiveDates ? 'Change Dates' : 'Select Dates' }}
            </button>

            <div v-if="showDatePicker && currentStep === 1" class="mobile-date-picker">
              <label class="mobile-sub-label">From:</label>
              <input type="date" class="browser-default mobile-input" v-model="dateFrom" :min="form.dateOfFiling">
              
              <label class="mobile-sub-label" style="margin-top: 10px;">To:</label>
              <input type="date" class="browser-default mobile-input" v-model="dateTo" :min="dateFrom">
              
              <div class="right-align" style="margin-top: 10px;">
                <button type="button" class="btn-flat btn-small" @click="showDatePicker = false">Cancel</button>
                <button type="button" class="btn-small green darken-2" @click="formatInclusiveDates">Confirm</button>
              </div>
            </div>
            
            <input type="text" class="browser-default mobile-input" v-model="form.inclusiveDates" @input="errors.inclusiveDates = false" :readonly="currentStep === 2" style="margin-top: 10px;">
          </div>

          <div class="mobile-section">
            <label class="mobile-label" :class="{ 'error-text': errors.commutation }">6.D COMMUTATION</label>
            <div class="mobile-radio-group">
              <label>
                <input type="radio" class="with-gap" value="Not Requested" v-model="form.commutation" :disabled="currentStep === 2">
                <span>Not Requested</span>
              </label>
              <label>
                <input type="radio" class="with-gap" value="Requested" v-model="form.commutation" :disabled="currentStep === 2">
                <span>Requested</span>
              </label>
            </div>
          </div>

          <div class="mobile-section center-align" style="padding-bottom: 30px;">
            <label class="mobile-label center-align" :class="{ 'error-text': errors.signature }">APPLICANT SIGNATURE</label>
            <div v-if="signatureData" style="height: 60px; margin: 15px 0;">
              <img :src="signatureData" alt="Signature" style="max-height: 100%; object-fit: contain;">
            </div>
            <button 
              v-if="currentStep === 1" 
              type="button" 
              class="btn indigo darken-3 mobile-btn" 
              @click="openSignatureModal" 
            >
              <i class="material-icons left">draw</i> 
              {{ signatureData ? 'Retake Signature' : 'Draw Signature' }}
            </button>
            <div class="mobile-signature-line"></div>
            <span class="mobile-sub-label">(Signature of Applicant)</span>
          </div>

          <div class="mobile-section-header">7. DETAILS OF ACTION</div>

          <div class="mobile-section">
            <label class="mobile-label">7.A CERTIFICATION OF LEAVE CREDITS</label>
            <div style="display: flex; align-items: center; margin-bottom: 10px;">
              <span style="font-size: 0.9rem; margin-right: 10px;">As of:</span>
              <input disabled type="text" class="browser-default mobile-input" v-model="form.actionAsOf" style="flex: 1; margin: 0;">
            </div>

            <div class="mobile-table-card">
              <div class="mobile-table-row header-row">
                <div></div>
                <div>Vacation</div>
                <div>Sick</div>
              </div>
              <div class="mobile-table-row">
                <div>Earned</div>
                <div>{{ form.vacationEarned || '0.00' }}</div>
                <div>{{ form.sickEarned || '0.00' }}</div>
              </div>
              <div class="mobile-table-row">
                <div>Less Apps</div>
                <div>{{ form.vacationLess || '0.00' }}</div>
                <div>{{ form.sickLess || '0.00' }}</div>
              </div>
              <div class="mobile-table-row footer-row">
                <div>Balance</div>
                <div>{{ form.vacationBalance || '0.00' }}</div>
                <div>{{ form.sickBalance || '0.00' }}</div>
              </div>
            </div>

            <div class="center-align" style="margin-top: 30px;">
              <div class="mobile-signature-line"></div>
              <span class="mobile-sub-label">Administrative Officer V (HRMO III)</span>
            </div>
          </div>

          <div class="mobile-section">
            <label class="mobile-label">7.B RECOMMENDATION</label>
            <div class="mobile-radio-group">
              <label>
                <input disabled type="radio" class="with-gap" value="For approval" v-model="form.recommendation">
                <span>For approval</span>
              </label>
              <label>
                <input disabled type="radio" class="with-gap" value="For disapproval" v-model="form.recommendation">
                <span>For disapproval due to:</span>
              </label>
              <input disabled type="text" class="browser-default mobile-input" v-model="form.disapprovalReason1" style="margin-top: 5px;">
              <input disabled type="text" class="browser-default mobile-input" v-model="form.disapprovalReason2" style="margin-top: 5px;">
              <input disabled type="text" class="browser-default mobile-input" v-model="form.disapprovalReason3" style="margin-top: 5px;">
            </div>

            <div class="center-align" style="margin-top: 30px;">
              <div class="mobile-signature-line"></div>
              <span class="mobile-sub-label">{{ form.position === "CHIEF ADMINISTRATIVE OFFICER" ? "Assistant Regional Director" : "Immediate Supervisor"}}</span>
            </div>
          </div>

          <div class="mobile-section">
            <label class="mobile-label">7.C APPROVED FOR:</label>
            <div style="display: flex; align-items: center; margin-bottom: 10px;">
              <input disabled type="text" class="browser-default mobile-input" v-model="form.approvedDaysWithPay" style="width: 80px; margin: 0 10px 0 0; text-align: center;">
              <span style="font-size: 0.9rem;">days with pay</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 10px;">
              <input disabled type="text" class="browser-default mobile-input" v-model="form.approvedDaysWithoutPay" style="width: 80px; margin: 0 10px 0 0; text-align: center;">
              <span style="font-size: 0.9rem;">days without pay</span>
            </div>
            <div style="display: flex; align-items: center;">
              <input disabled type="text" class="browser-default mobile-input" v-model="form.approvedOthers" style="width: 80px; margin: 0 10px 0 0; text-align: center;">
              <span style="font-size: 0.9rem;">others (Specify)</span>
            </div>
          </div>

          <div class="mobile-section">
            <label class="mobile-label">7.D DISAPPROVED DUE TO:</label>
            <input disabled type="text" class="browser-default mobile-input" v-model="form.finalDisapprovalReason1" style="margin-bottom: 5px;">
            <input disabled type="text" class="browser-default mobile-input" v-model="form.finalDisapprovalReason2" style="margin-bottom: 5px;">
            <input disabled type="text" class="browser-default mobile-input" v-model="form.finalDisapprovalReason3" style="margin-bottom: 5px;">
            <input disabled type="text" class="browser-default mobile-input" v-model="form.finalDisapprovalReason4">
            
            <div class="center-align" style="margin-top: 30px;">
              <div class="mobile-signature-line"></div>
            </div>
          </div>

          <div class="mobile-section center-align">
            <button v-if="currentStep === 1" data-target="uploadModal" class="btn modal-trigger grey darken-3 mobile-btn" style="margin-bottom: 15px;">
              <i class="material-icons left">attach_file</i> Upload Medical Cert / Docs
            </button>
            <div v-if="form.supportingDocument" class="green-text text-darken-2" style="font-weight: bold; font-size: 0.9rem;">
              <i class="material-icons tiny">check_circle</i> Attached: {{ form.supportingDocument.name }}
            </div>
          </div>

        </div>
      </div>

      <!-- ========================================================================================= -->
      <!-- RESPONSIVE WIZARD BUTTONS (Works for both layouts) -->
      <!-- ========================================================================================= -->
      <div class="wizard-actions z-depth-1 white center-align" style="padding: 20px; border-radius: 8px;">
        <h6 v-if="currentStep === 2" class="green-text text-darken-2 bold" style="margin-bottom: 15px;">Please review the form above before submitting.</h6>
        
        <button v-if="currentStep === 2" class="btn-flat grey-text text-darken-2" @click="currentStep = 1" style="border: 1px solid #ccc; margin: 5px;">
          <i class="material-icons left">edit</i> Edit Form
        </button>
        
        <button v-if="currentStep === 1" class="btn indigo darken-3 waves-light" @click="currentStep = 2" style="margin: 5px;">
          Next Step <i class="material-icons right">arrow_forward</i>
        </button>
        
        <button v-if="currentStep === 2" class="btn green darken-2 waves-light" @click="submitForm" :disabled="isSubmitting" style="margin: 5px;">
          {{ isSubmitting ? 'Submitting...' : 'Confirm & Submit Form' }} 
          <i v-if="!isSubmitting" class="material-icons right">check</i>
        </button>
      </div>

    </div>

    <!-- Modals (Outside form-wrapper to prevent layout conflicts) -->
    <div id="uploadModal" class="modal custom-modal-center" style="max-width: 500px;">
      <div class="modal-content left-align">
        <h4>Upload Supporting Document</h4>
        <p>Please attach any required medical certificates, clearances, or proof of travel (PDF, JPG, or PNG).</p>
        <p class="grey-text"><i>Note: You do not need to upload the Leave Form itself; the system will generate it automatically upon submission.</i></p>
        
        <div class="file-field input-field">
          <div class="btn indigo darken-3">
            <span>Choose File</span>
            <input type="file" @change="handleFileUpload" ref="fileInput" accept=".pdf,.jpg,.jpeg,.png">
          </div>
          <div class="file-path-wrapper">
            <input class="file-path validate" type="text" placeholder="Upload a file or photo">
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="modal-close waves-red btn-flat" @click="clearFile">Clear & Close</button>
        <button class="modal-close waves-green btn-flat indigo-text text-darken-3 bold">Confirm Selection</button>
      </div>
    </div>

    <div id="signatureModal" class="modal custom-modal-center" style="max-width: 700px !important;">
      <div class="modal-content center-align">
        <h4>Draw Your Signature</h4>
        <p class="grey-text">Please sign clearly in the box below using your mouse or finger.</p>
        
        <div class="canvas-container z-depth-1" style="position: relative; width: 100%; max-width: 600px; margin: 0 auto; overflow: hidden;">
          
          <div style="position: absolute; bottom: 25%; left: 10%; width: 80%; border-bottom: 2px solid #bdbdbd; pointer-events: none;"></div>
          <div style="position: absolute; bottom: 10%; width: 100%; text-align: center; color: #9e9e9e; font-size: 0.85rem; pointer-events: none; font-style: italic;">
            Sign above the line
          </div>

          <canvas 
            ref="signatureCanvas" 
            width="600" 
            height="250" 
            @mousedown.prevent="startDrawing" 
            @mousemove.prevent="draw" 
            @mouseup.prevent="stopDrawing" 
            @mouseleave.prevent="stopDrawing"
            @touchstart.prevent="startDrawingTouch"
            @touchmove.prevent="drawTouch"
            @touchend.prevent="stopDrawing"
            style="position: relative; z-index: 5; background: transparent; width: 100%; height: auto; cursor: crosshair;">
          </canvas>
        </div>

      </div>
      <div class="modal-footer">
        <button class="btn-flat waves-red" @click="clearSignature">Clear Signature</button>
        <!-- NEW BUTTONS -->
        <button class="btn-flat" @click="$refs.leaveSigUpload.click()">Upload PNG</button>
        <button class="btn-flat" @click="useLastSignature">Use my last signature</button>
        <input ref="leaveSigUpload" type="file" accept="image/png" style="display:none" @change="onSigUpload" />
        <!-- END NEW BUTTONS -->
        <button class="btn-flat modal-close waves-green indigo-text text-darken-3 bold" @click="saveSignature">Save Signature</button>
      </div>
    </div>

    <!-- NO BALANCE MODAL -->
    <div id="noBalanceModal" class="modal custom-modal-center" style="width: 90%; max-width: 450px !important; border-radius: 8px;">
      <div class="modal-content center-align">
        <i class="material-icons orange-text text-darken-2" style="font-size: 4rem; margin-bottom: 10px;">info_outline</i>
        <h5 style="margin-top: 0; font-weight: bold;">Leave Balances Not Found</h5>
        <p class="grey-text text-darken-2" style="font-size: 1.1rem; margin-top: 15px;">
          You currently do not have a leave balance record in the system. Please request your administrator to set up your current leave credits before applying.
        </p>
      </div>
      <div class="modal-footer" style="display: flex; justify-content: center; gap: 15px; padding-bottom: 25px;">
        <button class="btn-flat waves-effect grey-text text-darken-2" @click="navigateBack" :disabled="isRequestingBalance" style="font-weight: bold;">
          Cancel
        </button>
        <button class="btn blue darken-2 waves-light" @click="requestBalanceSetup" :disabled="isRequestingBalance" style="border-radius: 4px;">
          <span v-if="isRequestingBalance">Requesting...</span>
          <span v-else>Request Setup</span>
        </button>
      </div>
    </div>

  </FormLayout>
</template>

<script>
// Logic remains identical to ensure flawless functionality
import FormLayout from '@/views/layout/FormLayout.vue'; 
import api from '@/axios';
import { PDFDocument } from 'pdf-lib';

export default {
  name: "LeaveApplicationForm",
  components: { FormLayout },
  data() {
    return {
      isLoading: true,
      currentStep: 1,
      isSubmitting: false,
      showDatePicker: false,
      dateFrom: '',
      dateTo: '',
      
      signatureData: null,
      isDrawing: false,
      ctx: null,

      isRequestingBalance: false,
      balances: null,

      errors: {
        office: false, lastName: false, firstName: false,
        dateOfFiling: false, position: false, leaveType: false,
        leaveDetails: false, workingDays: false, inclusiveDates: false,
        commutation: false, signature: false   
      },
      form: {
        office: '', lastName: '', firstName: '', middleName: '',
        dateOfFiling: '', position: '', salary: '',
        leaveType: '', leaveTypeOthers: '',
        leaveDetails: '', leaveDetailsSpecifyPH: '', leaveDetailsSpecifyAbroad: '', 
        sickSpecifyHospital: '', sickSpecifyOutPatient: '',
        womenSpecify: '', othersspecify: '',
        workingDays: '', inclusiveDates: '', commutation: '',
        actionAsOf: '', vacationEarned: '', sickEarned: '',
        vacationLess: '', sickLess: '', vacationBalance: '', sickBalance: '',
        recommendation: '', disapprovalReason1: '', disapprovalReason2: '', disapprovalReason3: '', 
        approvedDaysWithPay: '', approvedDaysWithoutPay: '', approvedOthers: '',
        finalDisapprovalReason1: '', finalDisapprovalReason2: '', finalDisapprovalReason3: '', finalDisapprovalReason4: '',
        supportingDocument: null 
      }
    };
  },
  methods: {
    openSignatureModal() {
      this.errors.signature = false;
      const modal = document.getElementById('signatureModal');
      const instance = window.M.Modal.getInstance(modal);
      instance.open();
      
      this.$nextTick(() => {
        const canvas = this.$refs.signatureCanvas;
        this.ctx = canvas.getContext('2d');
        if (!this.signatureData) this.clearSignature();
      });
    },
    startDrawing(e) {
      this.isDrawing = true;
      this.draw(e);
    },
    draw(e) {
      if (!this.isDrawing) return;
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;   
      const scaleY = canvas.height / rect.height; 
      
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      this.ctx.lineWidth = 3;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = '#000';

      this.ctx.lineTo(x, y);
      this.ctx.stroke();
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);
    },
    startDrawingTouch(e) {
      if (!e.touches || e.touches.length === 0) return;
      this.isDrawing = true;
      this.drawTouch(e);
    },
    drawTouch(e) {
      if (!this.isDrawing || !e.touches || e.touches.length === 0) return;
      
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;   
      const scaleY = canvas.height / rect.height; 
      
      const x = (e.touches[0].clientX - rect.left) * scaleX;
      const y = (e.touches[0].clientY - rect.top) * scaleY;

      this.ctx.lineWidth = 3;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = '#000';

      this.ctx.lineTo(x, y);
      this.ctx.stroke();
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);
    },
    stopDrawing() {
      this.isDrawing = false;
      if (this.ctx) this.ctx.beginPath();
    },
    clearSignature() {
      if (this.ctx) {
        this.ctx.clearRect(0, 0, this.$refs.signatureCanvas.width, this.$refs.signatureCanvas.height);
      }
      this.signatureData = null;
    },
    saveSignature() {
      const canvas = this.$refs.signatureCanvas;
      const dataUrl = canvas.toDataURL('image/png');
      this.signatureData = dataUrl;
      
      // Remember this as the user's reusable "last signature".
      api.post("/my-signature", { signature: dataUrl }).catch(() => {});
    },

    // Draw an image (data URL) onto the signature canvas.
    drawImageOntoSigCanvas(dataUrl) {
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const w = img.width * scale, h = img.height * scale;
        ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      };
      img.src = dataUrl;
    },

    // Upload a PNG as the signature (optional alternative to drawing).
    onSigUpload(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file || file.type !== "image/png") return;
      const reader = new FileReader();
      reader.onload = (ev) => this.drawImageOntoSigCanvas(ev.target.result);
      reader.readAsDataURL(file);
    },

    // Load the user's most recent signature onto the canvas.
    async useLastSignature() {
      try {
        const res = await api.get("/my-last-signature");
        const dataUrl = res.data && res.data.signature;
        if (!dataUrl) {
          if (window.M) window.M.toast({ html: "No saved signature yet — draw or upload one.", classes: "red rounded" });
          return;
        }
        this.drawImageOntoSigCanvas(dataUrl);
      } catch (e) {
        if (window.M) window.M.toast({ html: "Could not load your last signature.", classes: "red rounded" });
      }
    },

    calculateWorkingDays(startDate, endDate) {
      let count = 0;
      let curDate = new Date(startDate.getTime());
      
      while (curDate <= endDate) {
        const dayOfWeek = curDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) count++;
        curDate.setDate(curDate.getDate() + 1);
      }
      return count;
    },

    formatInclusiveDates() {
      if (!this.dateFrom) {
        if (window.M) window.M.toast({ html: 'Please select a "From" date.', classes: 'red' });
        return;
      }
      const fromD = new Date(this.dateFrom);
      fromD.setHours(0, 0, 0, 0);

      const toD = this.dateTo ? new Date(this.dateTo) : null;
      if (toD) toD.setHours(0, 0, 0, 0);

      const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

      if (!toD) {
        this.form.inclusiveDates = `${monthNames[fromD.getMonth()]} ${fromD.getDate()}, ${fromD.getFullYear()}`;
        this.form.workingDays = this.calculateWorkingDays(fromD, fromD);
      } else {
        if (toD < fromD) {
            if (window.M) window.M.toast({ html: '"To" date cannot be before "From" date.', classes: 'red' });
            return;
        }
        if (fromD.getFullYear() === toD.getFullYear() && fromD.getMonth() === toD.getMonth()) {
          if (fromD.getDate() === toD.getDate()) {
              this.form.inclusiveDates = `${monthNames[fromD.getMonth()]} ${fromD.getDate()}, ${fromD.getFullYear()}`;
          } else {
              this.form.inclusiveDates = `${monthNames[fromD.getMonth()]} ${fromD.getDate()}-${toD.getDate()}, ${fromD.getFullYear()}`;
          }
        } else if (fromD.getFullYear() === toD.getFullYear()) {
          this.form.inclusiveDates = `${monthNames[fromD.getMonth()]} ${fromD.getDate()} - ${monthNames[toD.getMonth()]} ${toD.getDate()}, ${fromD.getFullYear()}`;
        } else {
          this.form.inclusiveDates = `${monthNames[fromD.getMonth()]} ${fromD.getDate()}, ${fromD.getFullYear()} - ${monthNames[toD.getMonth()]} ${toD.getDate()}, ${toD.getFullYear()}`;
        }
        this.form.workingDays = this.calculateWorkingDays(fromD, toD);
      }

      if (this.form.workingDays === 0 && window.M) {
         window.M.toast({ html: 'Selected dates fall entirely on a weekend. Working day/s is 0.', classes: 'orange darken-2' });
      }

      this.errors.inclusiveDates = false;
      this.errors.workingDays = false;
      this.showDatePicker = false;
    },

    handleAutoTab(currentValue, maxLength, nextRefName) {
      if (currentValue && currentValue.length >= maxLength) {
        const nextInput = this.$refs[nextRefName];
        if (nextInput) {
          nextInput.focus();
        }
      }
    },
    
    handleBackspace(event, currentValue, previousRefName) {
      if (event.key === 'Backspace' && currentValue.length === 0) {
        const prevInput = this.$refs[previousRefName];
        if (prevInput) {
          prevInput.focus();
        }
      }
    },

    handleFileUpload(event) {
      if (event.target.files.length > 0) {
        this.form.supportingDocument = event.target.files[0];
      }
    },
    
    clearFile() {
      this.form.supportingDocument = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      const filePathInput = document.querySelector('.file-path');
      if (filePathInput) filePathInput.value = '';
    },

    async getLoggedUser() {
      this.isLoading = true; 
      const loggedUser = localStorage.getItem('hrdos_user');
      const user = JSON.parse(loggedUser);
      if(user && user.employee) { 
        const loggedUserId = JSON.parse(loggedUser).employee.id;
        await api.get(`/employee/${loggedUserId}`)
          .then((response) => {
            const employeeData = response.data;
            this.form.office = employeeData.assigned_office?.name || '';
            this.form.lastName = employeeData.last_name || '';
            this.form.firstName = employeeData.first_name || '';
            this.form.middleName = employeeData.middle_name || '';
            this.form.position = employeeData.position?.title || '';
            this.form.salary = employeeData.salary_grade?.salary_amount || employeeData.base_salary || '';
          })
          .catch(error => console.error('Error fetching employee data:', error));
      }
      this.isLoading = false; 
    },
    
    async checkLeaveBalance() {
      try {
        const response = await api.get('/leave-dashboard');
        this.balances = response.data.leave_balances;

        if (!this.balances) {
          this.$nextTick(() => {
            if (window.M) {
              const modal = document.getElementById('noBalanceModal');
              let instance = window.M.Modal.getInstance(modal);
              
              instance = window.M.Modal.init(modal, { dismissible: false });

              instance.open();
            }
          });
        }
      } catch (error) {
        console.error("Error checking leave balances:", error);
      }
    },
    
    async requestBalanceSetup() {
      this.isRequestingBalance = true;

      try {
        await api.post('/leave-balances/request-setup');
        
        if (window.M) {
          window.M.toast({ html: 'Request sent to administrator queue.', classes: 'green' });
        }
        

      } catch (error) {
        const errorMsg = error.response?.data?.error || "Failed to send request.";
        if (window.M) window.M.toast({ html: errorMsg, classes: 'red' });
      } finally {
        this.isRequestingBalance = false;
        this.navigateBack();
      }
    },

    navigateBack() {
      if (window.M) {
        const modal = document.getElementById('noBalanceModal');
        const instance = window.M.Modal.getInstance(modal);
        if (instance && instance.isOpen) {
          instance.close();
        }
      }
      setTimeout(() => {
        if (window.history.length > 1) {
          this.$router.back();
        } else {
          this.$router.push('/'); 
        }
      }, 300);
    },

    handleValidationError(fieldName, message) {
      if (window.M) window.M.toast({ html: message, classes: 'red' });
      else alert(message);

      Object.keys(this.errors).forEach(key => this.errors[key] = false);
      if (fieldName in this.errors) this.errors[fieldName] = true;
      this.currentStep = 1;

      this.$nextTick(() => {
        const errorLabel = document.querySelector('.error-text');
        if (errorLabel && errorLabel.scrollIntoView) {
          errorLabel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Find visible ref (mobile vs desktop)
        const targetElement = this.$refs[fieldName];
        if (targetElement) {
          const elements = Array.isArray(targetElement) ? targetElement : [targetElement];
          const el = elements.find(e => e.offsetParent !== null) || elements[0];
          if (el && el.focus) el.focus();
        }
      });
    },

    async generateFilledPDF() {
      const url = this.form.position === 'CHIEF ADMINISTRATIVE OFFICER' ? '/documents/leave-form-chiefs.pdf' : '/documents/leave-form-staff.pdf'; 
      
      const existingPdfBytes = await fetch(url).then(res => {
        if (!res.ok) throw new Error("Could not find the PDF file at that path.");
        return res.arrayBuffer();
      });

      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pdfForm = pdfDoc.getForm();
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];

      const fillText = (fieldName, value) => {
        if (value === null || value === undefined || value === '') return;
        try { pdfForm.getTextField(fieldName).setText(value.toString()); } 
        catch (err) { /* ignore missing fields */ }
      };

      const lockField = (fieldName) => {
        try { pdfForm.getTextField(fieldName).enableReadOnly(); } 
        catch (err) { /* ignore */ }
      };

      fillText('txt-office', this.form.office);
      fillText('txt-lastname', this.form.lastName);
      fillText('txt-firstname', this.form.firstName);
      fillText('txt-middlename', this.form.middleName);
      fillText('txt-position', this.form.position);
      fillText('txt-salary', this.form.salary);
      fillText('txt-dateoffiling', this.form.dateOfFiling);
      fillText('txt-applieddays', this.form.workingDays + ' day/s');
      fillText('txt-inclusivedates', this.form.inclusiveDates);
      fillText('txt-leavetype-other', this.form.leaveTypeOthers);
      
      if(this.form.leaveDetails === 'Within the Philippines') fillText('txt-phspecify', this.form.leaveDetailsSpecifyPH); 
      if(this.form.leaveDetails === 'Abroad') fillText('txt-abroadspecify', this.form.leaveDetailsSpecifyAbroad);
      
      fillText('txt-hospitalspecify', this.form.sickSpecifyHospital);
      fillText('txt-outpatientspecify', this.form.sickSpecifyOutPatient);
      fillText('txt-womenspecify', this.form.womenSpecify);
      fillText('txt-otherspecify', this.form.othersspecify);

      const leaveTypeMap = {
        'Vacation Leave': 'cb1', 'Mandatory/Forced Leave': 'cb2', 'Sick Leave': 'cb3',
        'Maternity Leave': 'cb4', 'Paternity Leave': 'cb5', 'Special Privilege Leave': 'cb6',
        'Solo Parent Leave': 'cb7', 'Study Leave': 'cb8', '10-Day VAWC Leave': 'cb9',
        'Rehabilitation Privilege': 'cb10', 'Special Leave Benefits for Women': 'cb11',
        'Special Emergency Leave': 'cb12', 'Adoption Leave': 'cb13', 'Others': 'cb14'
      };

      const leaveDetailsMap = {
        'Within the Philippines': 'cb15', 'Abroad': 'cb16', 'In Hospital': 'cb17',
        'Out Patient': 'cb18', "Completion of Master's Degree": 'cb19',
        'BAR/Board Examination Review': 'cb20', 'Monetization': 'cb21', 'Terminal': 'cb22'
      };

      const commutationMap = {
        'Not Requested': 'cb23',
        'Requested': 'cb24'
      };

      const checkNativeBox = (checkboxName) => {
        if (!checkboxName) return; 
        try { pdfForm.getCheckBox(checkboxName).check(); } 
        catch (err) { /* ignore */ }
      };

      checkNativeBox(leaveTypeMap[this.form.leaveType]);
      checkNativeBox(leaveDetailsMap[this.form.leaveDetails]);
      checkNativeBox(commutationMap[this.form.commutation]);

      if (this.signatureData) {
        try {
          const sigImageBytes = await fetch(this.signatureData).then(res => res.arrayBuffer());
          const sigImage = await pdfDoc.embedPng(sigImageBytes);
          
          firstPage.drawImage(sigImage, {
            x: 375,     
            y: 310,     
            width: 140, 
            height: 50, 
          });
        } catch(err) {
          console.error("Could not embed signature onto PDF", err);
        }
      }

      const readOnlyTextFields = [
        'txt-office', 'txt-lastname', 'txt-firstname', 'txt-middlename',
        'txt-position', 'txt-salary', 'txt-dateoffiling', 'txt-applieddays',
        'txt-inclusivedates', 'txt-leavetype-other', 'txt-phspecify',
        'txt-abroadspecify', 'txt-hospitalspecify', 'txt-outpatientspecify',
        'txt-womenspecify', 'txt-otherspecify', 'txt-cert-Ic-asof', 'txt-total-earnedVLC', 
        'txt-total-earnedSLC', 'txt-lessVLC', 'txt-lessSLC', 'txt-balanceVLC', 
        'txt-balanceSLC', 'txt-disapprove-reason1', 'txt-disapprove-reason2', 
        'txt-disapprove-reason3', 'txt-disapproveddueto1', 'txt-disapproveddueto2', 
        'txt-disapproveddueto3', 'txt-disapproveddueto4', 'txt-dayswithpay', 'txt-dayswopay', 
        'txt-7c-others',
      ];
      readOnlyTextFields.forEach(fieldName => lockField(fieldName));

      const readOnlyCheckBoxes = [
        'cb1', 'cb2', 'cb3', 'cb4', 'cb5',
        'cb6', 'cb7', 'cb8', 'cb9', 'cb10',
        'cb11', 'cb12', 'cb13', 'cb14', 'cb15',
        'cb16', 'cb17', 'cb18', 'cb19', 'cb20',
        'cb21', 'cb22', 'cb23', 'cb24', 'cb25',
        'cb26',
      ];

      readOnlyCheckBoxes.forEach(fieldName => {
        try { 
          pdfForm.getCheckBox(fieldName).enableReadOnly(); 
        } catch (err) { 
          try { pdfForm.getRadioGroup(fieldName).enableReadOnly(); } catch (e) { /* ignore */ }
        }
      });

      pdfForm.updateFieldAppearances();

      const today = new Date();
      let currentDate = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const formatted_date = currentDate.replace(/, /g, '_').replace(/ /g, '_');
      
      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
      
      return new File([pdfBlob], `${this.form.lastName}_${this.form.firstName}_leave_application_${formatted_date}.pdf`, { type: 'application/pdf' });
    },

    async submitForm() {
      if (this.isSubmitting) return;

      if (!this.form.office) return this.handleValidationError('office', 'Please enter your Office/Department.');
      if (!this.form.lastName) return this.handleValidationError('lastName', 'Please enter your Last Name.');
      if (!this.form.firstName) return this.handleValidationError('firstName', 'Please enter your First Name.');
      if (!this.form.dateOfFiling) return this.handleValidationError('dateOfFiling', 'Please enter Date of Filing.');
      if (!this.form.position) return this.handleValidationError('position', 'Please enter your Position.');
      if (!this.form.leaveType) return this.handleValidationError('leaveType', 'Please select a Type of Leave.');
      if (!this.form.leaveDetails && !this.form.othersspecify) return this.handleValidationError('leaveDetails', 'Please select or specify the Details of Leave.');
      if (!this.form.workingDays) return this.handleValidationError('workingDays', 'Please enter the number of Working Days.');
      if (!this.form.inclusiveDates) return this.handleValidationError('inclusiveDates', 'Please enter the Inclusive Dates.');
      if (!this.form.commutation) return this.handleValidationError('commutation', 'Please select your Commutation preference.');
      if (!this.signatureData) return this.handleValidationError('signature', 'Please draw your signature before submitting.');

      this.isSubmitting = true;

      const today = new Date();
      let currentDate = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const formatted_date = currentDate.replace(/, /g, '_').replace(/ /g, '_');

      if (window.M) window.M.toast({ html: 'Generating and submitting document...', classes: 'blue' });

      try {
        const generatedPdfFile = await this.generateFilledPDF();
        const formData = new FormData();

        Object.keys(this.form).forEach(key => {
          if (key !== 'supportingDocument' && this.form[key] !== null && this.form[key] !== '') {
            const snakeCaseKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
            formData.append(snakeCaseKey, this.form[key]);
          }
        });

        formData.append('pdf_file', generatedPdfFile);

        if (this.dateFrom) formData.append('start_date', this.dateFrom);
        if (this.dateTo) formData.append('end_date', this.dateTo || this.dateFrom);

        if (this.form.supportingDocument) {
          formData.append('supporting_document', this.form.supportingDocument);
        }

        await api.post('/leave-applications', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const pdfUrl = URL.createObjectURL(generatedPdfFile);
        const link = document.createElement('a');
        link.href = pdfUrl;
        const downloadName = `${this.form.lastName}_${this.form.firstName}_leave_application_${formatted_date}.pdf`;
        link.setAttribute('download', downloadName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (window.M) {
          window.M.toast({ html: 'Leave Application Submitted Successfully!', classes: 'green' });
        }
        
        setTimeout(() => {
          this.$router.push('/my-leave-requests');
          URL.revokeObjectURL(pdfUrl); 
        }, 1500);

      } catch (error) {
        console.error("Error submitting form:", error);
        
        if (error.response && error.response.status === 422 && error.response.data.errors) {
            const errors = error.response.data.errors;
            const firstErrorKey = Object.keys(errors)[0]; 
            const errorMessage = errors[firstErrorKey][0];
            const camelCaseKey = firstErrorKey.replace(/_([a-z])/g, g => g[1].toUpperCase()); 
            return this.handleValidationError(camelCaseKey, errorMessage);
        }

        let errorMsg = "Failed to submit application. Please check your inputs.";
        if (error.response?.data?.message) {
            errorMsg = error.response.data.message;
        }
        alert(errorMsg);
        
      } finally {
        this.isSubmitting = true;
      }
    }
  },
  watch: {
    'form.leaveType'(newValue) {
      this.errors.leaveType = false;

      if (newValue !== 'Others') this.form.leaveTypeOthers = '';
      if (newValue !== 'Special Leave Benefits for Women') this.form.womenSpecify = '';
      
      this.form.leaveDetails = '';
      this.form.leaveDetailsSpecifyPH = '';
      this.form.leaveDetailsSpecifyAbroad = '';
      this.form.sickSpecifyHospital = '';
      this.form.sickSpecifyOutPatient = '';
    },
    
    'form.leaveDetails'(newValue) {
      this.errors.leaveDetails = false; 

      if (newValue) this.form.othersspecify = ''; 

      if (newValue !== 'Within the Philippines') this.form.leaveDetailsSpecifyPH = '';
      if (newValue !== 'Abroad') this.form.leaveDetailsSpecifyAbroad = '';
      if (newValue !== 'In Hospital') this.form.sickSpecifyHospital = '';
      if (newValue !== 'Out Patient') this.form.sickSpecifyOutPatient = '';
    },

    'form.womenSpecify'(newValue) {
      if (newValue && newValue.trim().length > 0) {
        this.form.leaveDetails = '';
        this.form.leaveDetailsSpecifyPH = '';
        this.form.leaveDetailsSpecifyAbroad = '';
        this.form.sickSpecifyHospital = '';
        this.form.sickSpecifyOutPatient = '';
        this.form.othersspecify = '';
      }
    },

    'form.othersspecify'(newValue) {
      this.errors.leaveDetails = false; 

      if (newValue && newValue.trim().length > 0) {
        this.form.leaveDetails = ''; 
        this.form.leaveDetailsSpecifyPH = '';
        this.form.leaveDetailsSpecifyAbroad = '';
        this.form.sickSpecifyHospital = '';
        this.form.sickSpecifyOutPatient = '';
        this.form.womenSpecify = ''; 
      }
    },
    
    'form.commutation'(newValue) {
      if(newValue !== '') this.errors.commutation = false;
    }
  },
  mounted() {
    this.checkLeaveBalance(); 
    this.getLoggedUser();
    this.form.dateOfFiling = new Date().toISOString().split('T')[0];
    
    if (window.M) {
      const modals = document.querySelectorAll('.modal');
      window.M.Modal.init(modals);
    }
  }
};
</script>

<style scoped>
/* ========================================================= */
/* STRICT PAPER FORM LAYOUT (Replicates PDF)                 */
/* ========================================================= */

.form-wrapper {
  width: 100%;
  overflow-x: auto;
  padding: 20px;
}

.paper-form {
  color: #000;
  font-family: Arial, sans-serif;
  margin: 0 auto 20px auto; 
  padding-bottom: 30px;
  transition: opacity 0.3s ease;
  width: 1010px; 
  min-width: 1010px; 
}

.form-inputs {
  margin-right: auto;
  margin-left: auto;
  display: block;
  width: 95%;
}

.read-only-mode {
  opacity: 0.85;
  pointer-events: none; 
}

.flex-row {
  display: flex;
  width: 100%;
}

.w-10 { width: 10%; }
.w-20 { width: 20%; }
.w-30 { width: 30%; }
.w-35 { width: 35%; }
.w-40 { width: 40%; }
.w-45 { width: 45%; }
.w-50 { width: 50%; }
.w-55 { width: 55%; }
.w-70 { width: 70%; }

.border-top { border-top: 2px solid #000; }
.border-right { border-right: 2px solid #000; }

.padding-sm { padding: 8px 12px; }
.padding-md { padding: 12px 16px; }

/* HEADER STYLES */
.form-header {
  padding: 5px;
}

.header-left {
  width: 25%;
  font-size: 0.75rem;
  font-weight: bold;
}
.header-right {
  width: 25%;
  font-size: 0.75rem;
  font-weight: bold;
}
.header-center {
  width: 50%;
}
.header-logo {
  height: 90px;
  width: auto;
}
.republic-text {
  font-size: 0.85rem;
  line-height: 21px;
  font-weight: bold;
  margin: 40px 0 10px 10px;
  text-align: left;
}
.form-title {
  letter-spacing: 1px;
  font-size: 1.3rem;
  margin: 0;
}
.stamp-box {
  height: 30px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content:  flex-start;
  margin: 29px 0 0 -59px;
  text-align: left;
  color: #777;
  font-weight: normal;
  font-size: 0.7rem;
}

/* SECTION HEADERS */
.section-header {
  background-color: #f0f0f0;
  padding: 5px 0;
  font-size: 1rem;
}

/* TYPOGRAPHY & INPUTS */
.cs-label {
  display: block;
  font-size: 0.75rem;
  font-weight: bold;
  color: #000;
  margin-bottom: 2px;
  text-align: left;
  white-space: nowrap;
}

.form-col {
  padding: 0;
  padding-left: 2px;
}

.sub-label {
  font-size: 0.8rem;
  color: #555;
}

.cs-input {
  border-bottom: 2px solid #000 !important;
  box-shadow: none !important;
  height: 1.8rem !important;
  margin: 0 !important;
  font-size: 0.9rem !important;
  font-family: Arial, sans-serif;
  color: #000 !important;
  background-color: transparent !important;
  padding: 0 !important;
  margin-bottom: 5px !important;
}

.cs-input:focus {
  border-bottom: 2px solid #000 !important;
}

.inline-line {
  border-bottom: 2px solid #000 !important;
  height: 1.2rem !important;
}

.full-width {
  width: 100%;
}

.name-grid {
  display: flex;
  gap: 10px;
}

.name-field {
  flex: 1;
  min-width: 0;
}

.name-field input {
  width: 100% !important;
}

.row1and2 { border: 2px solid black; margin-bottom: 3px; }
.row3 { border: 2px solid black; margin-bottom: 3px; }
.row4 { border: 2px solid black; }
.row5 { border: 2px solid black; border-top: none; margin-bottom: 3px; }
.row6 { border: 2px solid black; margin-bottom: 3px; }
.row7 { border: 2px solid black; }
.row8 { border: 2px solid black; border-top: none; border-bottom: none; }
.row9 { border: 2px solid black; border-top: none; }

/* ========================================================= */
/* SECTION 7 TABLE (Leave Credits)                           */
/* ========================================================= */

.cert-table {
  width: 90%;
  border-collapse: collapse;
  margin-top: 3px;
  margin-bottom: 10px;
  margin-left: 35px;
  display: block;
}
.cert-table th, .cert-table td {
  border: 1px solid #000;
  padding: 5px 8px;
  text-align: center;
  font-size: 0.85rem;
  color: #000;
}
.cert-table td:first-child {
  text-align: left;
  font-style: italic;
}
.table-input {
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
  height: 1.2rem !important;
  text-align: center !important;
  font-size: 0.85rem !important;
  background-color: transparent !important;
  color: #000 !important;
}

.table-input:focus {
  border-bottom: none !important;
  box-shadow: none !important;
}

/* ========================================================= */
/* STRICT PAPER-STYLE CHECKBOXES/RADIOS (LADDER STYLE)       */
/* ========================================================= */

.cs-custom-radios {
  display: flex;
  flex-direction: column;
  gap: 0; 
}

.cs-radio-row {
  display: flex;
  align-items: stretch; 
  cursor: pointer;
  color: #000;
  margin: 0 !important; 
  margin-left: 10px !important; 
}

/* Forcefully kill the native radio input */
.cs-radio-row input[type="radio"] {
  position: absolute !important;
  opacity: 0 !important;
  width: 0 !important;
  height: 0 !important;
  pointer-events: none !important;
}

/* The custom thick square box */
.cs-box {
  width: 28px; 
  border: 3px solid #000; 
  border-bottom: none; 
  background-color: #fff;
  margin-right: 12px;
  position: relative;
  flex-shrink: 0; 
  display: inline-block;
  box-sizing: border-box; 
}

/* Add the bottom border ONLY to the very last box to close the ladder */
.cs-radio-row:last-child .cs-box {
  border-bottom: 3px solid #000;
}

/* The checked state (perfectly centered checkmark) */
.cs-radio-row input[type="radio"]:checked + .cs-box::after {
  content: "✔"; 
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%); 
  font-size: 16px; 
  color: #000;
  font-weight: bold;
  line-height: 1; 
}

/* Text Styling */
.cs-text {
  font-size: 1rem;
  line-height: 1.8;
}

.cs-text strong {
  font-weight: 700;
  font-size: 0.85rem;
}

.cs-text small {
  font-size: 0.6rem;
  color: #000;
  font-weight: 400;
}

.others-row {
  align-items: stretch;
}

.others-text-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

.thick-line {
  border-bottom: 2px solid #000 !important;
}
.signature-line {
  margin: 0 auto;
  border-bottom: 2px solid #000 !important;
}

input {
  font-weight: bold !important;
  text-align: center !important;
}

.bold {
  font-weight: bold;
}

.error-text {
  color: #d32f2f !important;
  background-color: #d32f2f8c;
}

.border-red {
  border-bottom: 2px solid #d32f2f !important;
}

/* Custom Signature Canvas Container */
.canvas-container {
  display: inline-block; 
  border: 2px dashed #9e9e9e; 
  border-radius: 8px; 
  background-color: #fafafa;
  touch-action: none; 
  position: relative; 
}

/* ========================================================= */
/* MINIMALIST OVERLAY LOADER                                 */
/* ========================================================= */

.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6); /* Semi-transparent black */
  z-index: 100; /* Stays above the form inputs */
  border-radius: 2px; /* Matches z-depth if any */
}

.minimal-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: Arial, sans-serif;
  
  /* FIXED POSITION TO VIEWPORT */
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 101; 
}

.loader-text {
  font-size: 1.2rem;
  font-weight: bold;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #ffffff; /* White text for contrast */
  margin-bottom: 15px;
  animation: pulse 1.5s infinite;
}

.loader-line {
  width: 200px;
  height: 20px;
  background-color: rgba(255, 255, 255, 0.2); /* Faint white line */
  position: relative;
  overflow: hidden;
}

.loader-line::after {
  content: '';
  position: absolute;
  top: 0;
  left: -50px;
  width: 50px;
  height: 100%;
  background-color: #ffffff; /* Solid white slider */
  animation: slide 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

@keyframes slide {
  0% { left: -50px; }
  100% { left: 200px; }
}

/* Custom Centered Modal Override */
.custom-modal-center.modal {
  width: 90% !important;
  border-radius: 12px !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) scale(1) !important;
  margin: 0 !important;
  max-height: 90% !important;
  overflow-y: auto !important;
}

/* Materialize uses a scale transform for animation. We must account for the closed state. */
.custom-modal-center.modal:not(.open) {
  transform: translate(-50%, -50%) scale(0.8) !important;
}

/* ========================================================= */
/* MOBILE/TABLET STACKING LAYOUT (Separate Div)              */
/* ========================================================= */
.mobile-paper-form {
  padding: 15px;
  border-radius: 12px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
}

.mobile-section-header {
  background-color: #1565c0;
  color: white;
  padding: 10px 15px;
  font-weight: bold;
  border-radius: 6px;
  margin: 25px 0 15px 0;
  font-size: 1rem;
  letter-spacing: 0.5px;
}

.mobile-section {
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
}

.mobile-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: #1565c0;
  margin-bottom: 8px;
}

.mobile-sub-label {
  display: block;
  font-size: 0.75rem;
  color: #757575;
  margin-bottom: 4px;
}

.mobile-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  box-sizing: border-box;
  color: #333;
}
.mobile-input:focus {
  border-color: #1565c0;
  outline: none;
}
.mobile-input[disabled] {
  background-color: #eeeeee;
  color: #757575;
}

.mobile-name-grid {
  display: flex;
  gap: 10px;
  flex-direction: column;
}

.mobile-hint {
  font-size: 0.85rem;
  font-weight: 600;
  color: #424242;
  margin: 5px 0 10px 0;
}

/* Standard Materialize Radio Overrides for Mobile */
.mobile-radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.mobile-radio-group label {
  display: flex;
  align-items: flex-start;
}
.mobile-radio-group span {
  font-size: 0.95rem;
  color: #212121;
  padding-left: 28px !important; /* Space for the native materialize radio */
  line-height: 1.4;
}

/* Mobile Tables */
.mobile-table-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
}
.mobile-table-row {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
}
.mobile-table-row:last-child {
  border-bottom: none;
}
.mobile-table-row > div {
  flex: 1;
  padding: 10px;
  text-align: center;
  font-size: 0.9rem;
}
.mobile-table-row > div:first-child {
  font-weight: bold;
  background-color: #f5f5f5;
  text-align: left;
  border-right: 1px solid #e0e0e0;
}
.header-row > div {
  font-weight: bold;
  background-color: #f5f5f5;
  border-right: 1px solid #e0e0e0;
}
.header-row > div:last-child {
  border-right: none;
}
.footer-row {
  background-color: #e8eaf6;
  font-weight: bold;
}

/* Misc Mobile */
.mobile-btn {
  width: 100%;
  border-radius: 6px;
  margin-top: 10px;
}
.mobile-signature-line {
  border-bottom: 2px solid #000;
  width: 80%;
  margin: 0 auto 5px auto;
}
.mobile-date-picker {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  margin-top: 10px;
}
</style>